import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Skeleton,
    Alert,
    Divider,
    useTheme,
} from '@mui/material';
import {
    TrendingUp,
    ShoppingCart,
    People,
    Inventory,
    AttachMoney,
    Warning,
    HourglassEmpty,
} from '@mui/icons-material';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import dashboardService from '../../services/dashboardService';
import { useCurrency } from '../../hooks/useCurrency';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatShortDate = (isoDate) => {
    if (!isoDate) return '';
    const [, month, day] = isoDate.split('-');
    return `${parseInt(day)}/${parseInt(month)}`;
};

const ORDER_STATUS_COLORS = {
    PENDING:    '#f59e0b',
    PROCESSING: '#3b82f6',
    SHIPPED:    '#8b5cf6',
    DELIVERED:  '#22c55e',
    CANCELLED:  '#ef4444',
    RETURN_REQUESTED:   '#f97316',
    EXCHANGE_REQUESTED: '#eab308',
    RETURNED:           '#64748b',
    EXCHANGED:          '#0ea5e9',
};

const PIE_COLORS = ['#f59e0b', '#3b82f6', '#8b5cf6', '#22c55e', '#ef4444'];

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ title, value, icon, color, subtitle, loading }) => {
    const theme = useTheme();

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography color="text.secondary" variant="caption" gutterBottom display="block">
                            {title}
                        </Typography>
                        {loading ? (
                            <Skeleton variant="text" width={100} height={40} />
                        ) : (
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {value}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography variant="caption" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: theme.palette.mode === 'dark'
                                ? `${color}.dark`
                                : `${color}.light`,
                            color: `${color}.main`,
                            flexShrink: 0,
                            ml: 1,
                        }}
                    >
                        {icon}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {children}
    </Typography>
);

const statusChip = (status, t) => (
    <Chip
        label={t(`orders.status.${status}`, status)}
        size="small"
        sx={{
            bgcolor: ORDER_STATUS_COLORS[status] + '22',
            color: ORDER_STATUS_COLORS[status],
            fontWeight: 600,
            fontSize: '0.7rem',
        }}
    />
);

// ─── Custom tooltip for the area chart ───────────────────────────────────────

const RevenueTooltip = ({ active, payload, label }) => {
    const { format: formatCurrency } = useCurrency();
    if (!active || !payload || !payload.length) return null;
    return (
        <Paper sx={{ p: 1.5, minWidth: 160 }} elevation={3}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatCurrency(payload[0]?.value)}
            </Typography>
            {payload[1] && (
                <Typography variant="caption" color="text.secondary">
                    {payload[1].value} orders
                </Typography>
            )}
        </Paper>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const Dashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { format: formatCurrency } = useCurrency();

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await dashboardService.getStats();
            setStats(data);
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to load dashboard data';
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // ── Revenue chart data: compact date labels ───────────────────
    const revenueChartData = (stats?.revenueByDay ?? []).map((pt) => ({
        date: formatShortDate(pt.date),
        revenue: parseFloat(pt.revenue ?? 0),
        orders: pt.orderCount,
    }));

    // ── Pie chart data ────────────────────────────────────────────
    const pieData = (stats?.ordersByStatus ?? []).map((item) => ({
        name: t(`orders.status.${item.status}`, item.status),
        value: item.count,
    }));

    const primaryColor = theme.palette.primary.main;

    return (
        <Box>
            {/* Header */}
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
                {t('dashboard.title')}
            </Typography>

            {error && !loading && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* ── KPI Cards ─────────────────────────────────────── */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title={t('dashboard.totalRevenue')}
                        value={loading ? '—' : formatCurrency(stats?.totalRevenue)}
                        icon={<AttachMoney />}
                        color="success"
                        subtitle={t('dashboard.revenueSubtitle')}
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title={t('dashboard.totalOrders')}
                        value={loading ? '—' : stats?.totalOrders?.toLocaleString()}
                        icon={<ShoppingCart />}
                        color="primary"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title={t('dashboard.pendingOrders')}
                        value={loading ? '—' : stats?.pendingOrders?.toLocaleString()}
                        icon={<HourglassEmpty />}
                        color="warning"
                        subtitle={t('dashboard.pendingSubtitle')}
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title={t('dashboard.customers')}
                        value={loading ? '—' : stats?.totalCustomers?.toLocaleString()}
                        icon={<People />}
                        color="info"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title={t('dashboard.activeProducts')}
                        value={loading ? '—' : stats?.totalActiveProducts?.toLocaleString()}
                        icon={<Inventory />}
                        color="secondary"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title={t('dashboard.lowStock')}
                        value={loading ? '—' : stats?.lowStockVariants?.toLocaleString()}
                        icon={<Warning />}
                        color="error"
                        subtitle={t('dashboard.lowStockSubtitle')}
                        loading={loading}
                    />
                </Grid>
            </Grid>

            {/* ── Charts Row ────────────────────────────────────── */}
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
                {/* Revenue Trend */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3 }}>
                        <SectionTitle>{t('dashboard.revenueTrend')}</SectionTitle>
                        {loading ? (
                            <Skeleton variant="rectangular" height={260} />
                        ) : (
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={revenueChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={primaryColor} stopOpacity={0.25} />
                                            <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 11 }}
                                        interval="preserveStartEnd"
                                        stroke={theme.palette.text.secondary}
                                    />
                                    <YAxis
                                        tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                                        tick={{ fontSize: 11 }}
                                        stroke={theme.palette.text.secondary}
                                        width={44}
                                    />
                                    <Tooltip content={<RevenueTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="revenue"
                                        stroke={primaryColor}
                                        strokeWidth={2}
                                        fill="url(#revenueGrad)"
                                        name="Revenue"
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Grid>

                {/* Orders by Status */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <SectionTitle>{t('dashboard.ordersByStatus')}</SectionTitle>
                        {loading ? (
                            <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto' }} />
                        ) : pieData.length === 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
                                <Typography color="text.secondary" variant="body2">{t('dashboard.noOrders')}</Typography>
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={240}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="45%"
                                        innerRadius={55}
                                        outerRadius={85}
                                        paddingAngle={3}
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell
                                                key={entry.name}
                                                fill={ORDER_STATUS_COLORS[entry.name] ?? PIE_COLORS[index % PIE_COLORS.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value, name) => [value, name]} />
                                    <Legend
                                        iconType="circle"
                                        iconSize={8}
                                        formatter={(value) => (
                                            <span style={{ fontSize: 12 }}>{value}</span>
                                        )}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* ── Tables Row ────────────────────────────────────── */}
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
                {/* Recent Orders */}
                <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <SectionTitle>{t('dashboard.recentOrders')}</SectionTitle>
                            <Typography
                                variant="caption"
                                sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => navigate('/orders')}
                            >
                                {t('common.viewAll')} →
                            </Typography>
                        </Box>
                        {loading ? (
                            [...Array(5)].map((_, i) => (
                                <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 1, borderRadius: 1 }} />
                            ))
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('dashboard.orderNumber')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('orders.customer')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('common.total')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('common.status')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(stats?.recentOrders ?? []).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                        {t('dashboard.noOrders')}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            (stats?.recentOrders ?? []).map((order) => (
                                                <TableRow
                                                    key={order.id}
                                                    hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => navigate(`/orders/${order.id}`)}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                                            {order.orderNumber}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{order.customerName}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {formatCurrency(order.totalAmount)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{statusChip(order.status, t)}</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>

                {/* Top Products */}
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <SectionTitle>{t('dashboard.topProducts')}</SectionTitle>
                            <Typography
                                variant="caption"
                                sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => navigate('/products')}
                            >
                                {t('common.viewAll')} →
                            </Typography>
                        </Box>
                        {loading ? (
                            [...Array(5)].map((_, i) => (
                                <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 1, borderRadius: 1 }} />
                            ))
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('dashboard.rank')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('products.product')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }} align="right">{t('dashboard.revenue')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }} align="right">{t('dashboard.units')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(stats?.topProducts ?? []).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                        {t('dashboard.noSalesData')}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            (stats?.topProducts ?? []).map((product, index) => (
                                                <TableRow key={product.productName} hover>
                                                    <TableCell>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: index === 0 ? 'warning.main'
                                                                     : index === 1 ? 'text.secondary'
                                                                     : 'text.disabled',
                                                            }}
                                                        >
                                                            #{index + 1}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                maxWidth: 160,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {product.productName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {formatCurrency(product.revenue)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" color="text.secondary">
                                                            {product.unitsSold}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;
